import React, { useState } from 'react';
import { fetchPopularityReport } from '../shared/api';
import { InlineSpinner } from '../shared/Spinner';
import LookPreviewCanvas from '../shared/LookPreviewCanvas';

const HoverPreview = ({ children, preview }) => {
  const [hover, setHover] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });

  const handleMouseEnter = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setPos({ x: rect.right + 8, y: rect.top });
    setHover(true);
  };

  return (
    <div
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setHover(false)}
      style={{ cursor: 'pointer' }}
    >
      {children}
      {hover && (
        <div style={{
          position: 'fixed', left: pos.x, top: pos.y,
          zIndex: 10001, background: '#fff', borderRadius: 6,
          boxShadow: '0 4px 20px rgba(0,0,0,.25)', padding: 4,
          border: '1px solid #ddd', pointerEvents: 'none'
        }}>
          {preview}
        </div>
      )}
    </div>
  );
};

const LOOK_COLORS = [
  '#1976d2', '#e91e63', '#4caf50', '#ff9800', '#9c27b0',
  '#00bcd4', '#795548', '#607d8b', '#f44336', '#8bc34a',
  '#3f51b5', '#ff5722', '#009688', '#cddc39', '#673ab7',
  '#ffc107', '#03a9f4', '#e040fb', '#00e676', '#ff6e40'
];

const BG_COLORS = [
  '#4caf50', '#2196f3', '#ff9800', '#e91e63', '#9c27b0',
  '#00bcd4', '#795548', '#607d8b', '#f44336', '#8bc34a',
  '#3f51b5', '#ff5722'
];

const PieChart = ({ data, colors, size = 180, hovered, onHover, renderPreview }) => {
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const total = data.reduce((sum, d) => sum + d.value, 0);
  if (total === 0) {
    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={size / 2 - 2} fill="#eee" stroke="#ddd" strokeWidth="1" />
        <text x={size / 2} y={size / 2 + 4} textAnchor="middle" fontSize="12" fill="#999">No data</text>
      </svg>
    );
  }

  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 4;
  let cumAngle = -Math.PI / 2;

  const slices = data.map((d, i) => {
    const angle = (d.value / total) * Math.PI * 2;
    const startAngle = cumAngle;
    cumAngle += angle;
    const endAngle = cumAngle;

    const largeArc = angle > Math.PI ? 1 : 0;
    const isHovered = hovered === i;
    const sliceR = isHovered ? r + 4 : r;
    const sx1 = cx + sliceR * Math.cos(startAngle);
    const sy1 = cy + sliceR * Math.sin(startAngle);
    const sx2 = cx + sliceR * Math.cos(endAngle);
    const sy2 = cy + sliceR * Math.sin(endAngle);

    const path = `M ${cx} ${cy} L ${sx1} ${sy1} A ${sliceR} ${sliceR} 0 ${largeArc} 1 ${sx2} ${sy2} Z`;

    return (
      <path
        key={i}
        d={path}
        fill={colors[i % colors.length]}
        opacity={hovered != null && !isHovered ? 0.5 : 1}
        stroke="#fff"
        strokeWidth="1.5"
        onMouseEnter={(e) => { setMousePos({ x: e.clientX, y: e.clientY }); onHover(i); }}
        onMouseMove={(e) => setMousePos({ x: e.clientX, y: e.clientY })}
        onMouseLeave={() => onHover(null)}
        style={{ cursor: 'pointer', transition: 'opacity .15s' }}
      />
    );
  });

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <svg width={size + 8} height={size + 8} viewBox={`-4 -4 ${size + 8} ${size + 8}`}>
        {slices}
      </svg>
      {hovered != null && renderPreview && (
        <div style={{
          position: 'fixed', left: mousePos.x + 12, top: mousePos.y - 60,
          zIndex: 10002, background: '#fff', borderRadius: 6,
          boxShadow: '0 4px 20px rgba(0,0,0,.25)', padding: 8,
          border: '1px solid #ddd', pointerEvents: 'none'
        }}>
          {renderPreview(hovered)}
        </div>
      )}
    </div>
  );
};

const PieChartLegend = ({ data, colors, hovered, onHover }) => (
  <div style={{ fontSize: 11, display: 'flex', flexDirection: 'column', gap: 3 }}>
    {data.map((d, i) => (
      <div
        key={i}
        onMouseEnter={() => onHover(i)}
        onMouseLeave={() => onHover(null)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
          opacity: hovered != null && hovered !== i ? 0.5 : 1,
          fontWeight: hovered === i ? 600 : 400,
          transition: 'opacity .15s'
        }}
      >
        <div style={{ width: 10, height: 10, borderRadius: 2, background: colors[i % colors.length], flexShrink: 0 }} />
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.label}</span>
        <span style={{ color: '#666', flexShrink: 0 }}>{d.value} ({d.pct}%)</span>
      </div>
    ))}
  </div>
);

const PopularityReport = ({ season, packageId, looks: allLooks }) => {
  const [showPanel, setShowPanel] = useState(false);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [hoveredLookSlice, setHoveredLookSlice] = useState(null);
  const [hoveredBgSlice, setHoveredBgSlice] = useState(null);

  const handleGenerate = async () => {
    setShowPanel(true);
    setLoading(true);
    setError(null);
    try {
      const data = await fetchPopularityReport(season, packageId);
      setReport(data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
    setLoading(false);
  };

  const barStyle = (pct) => ({
    width: `${Math.max(2, pct)}%`,
    height: 6,
    background: '#1976d2',
    borderRadius: 3,
    transition: 'width .3s'
  });

  const bgBarStyle = (pct) => ({
    ...barStyle(pct),
    background: '#4caf50'
  });

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={handleGenerate}
        style={{
          padding: '6px 14px', background: '#fff', border: '1px solid #ccc',
          borderRadius: 4, cursor: 'pointer', fontSize: 13, display: 'inline-flex',
          alignItems: 'center', gap: 5, whiteSpace: 'nowrap'
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
        Report
      </button>

      {showPanel && (
        <div
          onClick={() => setShowPanel(false)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,.5)', zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 8, padding: 24,
              width: 900, maxWidth: '95vw', maxHeight: '85vh', overflow: 'auto',
              boxShadow: '0 8px 32px rgba(0,0,0,.2)'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 16 }}>
                Popularity Report — {season}
              </h3>
              <button
                onClick={() => setShowPanel(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#999' }}
              >
                &times;
              </button>
            </div>

            {loading && (
              <div style={{ padding: 40, textAlign: 'center' }}>
                <InlineSpinner size={24} /> <span style={{ marginLeft: 8, color: '#666' }}>Loading order data...</span>
              </div>
            )}

            {error && (
              <div style={{ padding: 20, color: '#d32f2f', fontSize: 13 }}>{error}</div>
            )}

            {report && !loading && (() => {
              const lookPieItems = report.looks.filter(l => l.sheet_count > 0);
              const lookPieData = lookPieItems
                .map(l => ({ label: l.name, value: l.sheet_count, pct: l.percentage, lookTemplateId: l.look_template_id }));

              const bgTop12 = report.backgrounds
                .filter(b => b.sheet_count > 0)
                .slice(0, 12);
              const top12Total = bgTop12.reduce((s, b) => s + b.sheet_count, 0);
              const bgPieData = bgTop12.map(b => ({
                label: b.color || b.letter || `BG #${b.background_id}`,
                value: b.sheet_count,
                pct: top12Total > 0 ? (b.sheet_count / top12Total * 100).toFixed(2) : 0,
                imageUrl: b.image_url
              }));

              return (
              <>
                {/* Summary line */}
                <div style={{ fontSize: 12, color: '#666', marginBottom: 16, textAlign: 'center' }}>
                  <strong>{report.total_orders}</strong> orders &middot; <strong>{report.total_sheets}</strong> sheets &middot; <strong>{report.looks.length}</strong> looks &middot; <strong>{report.backgrounds.length}</strong> backgrounds &middot; avg <strong>{report.avg_poses_per_order}</strong> poses/order (range: {report.min_poses_per_order}–{report.max_poses_per_order})
                </div>

                {/* Pie charts side by side */}
                <div style={{ display: 'flex', gap: 24, marginBottom: 24 }}>
                  {/* Looks pie */}
                  <div style={{ flex: 1, background: '#f9f9f9', borderRadius: 6, padding: 16 }}>
                    <h4 style={{ margin: '0 0 12px', fontSize: 13, color: '#333', textAlign: 'center' }}>Looks Popularity</h4>
                    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                      <PieChart
                        data={lookPieData}
                        colors={LOOK_COLORS}
                        size={160}
                        hovered={hoveredLookSlice}
                        onHover={setHoveredLookSlice}
                        renderPreview={(idx) => {
                          const item = lookPieData[idx];
                          if (!item) return null;
                          const lookData = (allLooks || []).find(l => l.id === item.lookTemplateId);
                          return (
                            <div style={{ textAlign: 'center' }}>
                              {lookData && (
                                <div style={{ width: 120, height: 150, background: '#f0f0f0', borderRadius: 4, overflow: 'hidden', marginBottom: 6 }}>
                                  <LookPreviewCanvas look={lookData} width={120} height={150} allLooks={allLooks} />
                                </div>
                              )}
                              <div style={{ fontSize: 12, fontWeight: 600 }}>{item.label}</div>
                              <div style={{ fontSize: 11, color: '#666' }}>{item.value} sheets ({item.pct}%)</div>
                            </div>
                          );
                        }}
                      />
                      <div style={{ flex: 1, maxHeight: 180, overflow: 'auto' }}>
                        <PieChartLegend
                          data={lookPieData}
                          colors={LOOK_COLORS}
                          hovered={hoveredLookSlice}
                          onHover={setHoveredLookSlice}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Backgrounds pie */}
                  <div style={{ flex: 1, background: '#f9f9f9', borderRadius: 6, padding: 16 }}>
                    <h4 style={{ margin: '0 0 12px', fontSize: 13, color: '#333', textAlign: 'center' }}>Background Popularity (Top 12)</h4>
                    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                      <PieChart
                        data={bgPieData}
                        colors={BG_COLORS}
                        size={160}
                        hovered={hoveredBgSlice}
                        onHover={setHoveredBgSlice}
                        renderPreview={(idx) => {
                          const item = bgPieData[idx];
                          if (!item) return null;
                          return (
                            <div style={{ textAlign: 'center' }}>
                              {item.imageUrl ? (
                                <img src={item.imageUrl} alt={item.label} style={{
                                  width: 120, height: 120, objectFit: 'cover', borderRadius: 4, display: 'block', marginBottom: 6
                                }} />
                              ) : (
                                <div style={{
                                  width: 120, height: 120, borderRadius: 4, background: '#eee', marginBottom: 6,
                                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, color: '#999'
                                }}>{item.label}</div>
                              )}
                              <div style={{ fontSize: 12, fontWeight: 600 }}>{item.label}</div>
                              <div style={{ fontSize: 11, color: '#666' }}>{item.value} sheets ({item.pct}%)</div>
                            </div>
                          );
                        }}
                      />
                      <div style={{ flex: 1, maxHeight: 180, overflow: 'auto' }}>
                        <PieChartLegend
                          data={bgPieData}
                          colors={BG_COLORS}
                          hovered={hoveredBgSlice}
                          onHover={setHoveredBgSlice}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Looks */}
                <h4 style={{ margin: '0 0 10px', fontSize: 14, color: '#333' }}>
                  Looks by Popularity
                </h4>
                {report.looks.length === 0 ? (
                  <div style={{ padding: 16, color: '#999', fontSize: 12 }}>No look data found for this season.</div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 20 }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e0e0e0', textAlign: 'left' }}>
                        <th style={{ padding: '6px 8px', width: 30 }}>#</th>
                        <th style={{ padding: '6px 8px', width: 40 }}></th>
                        <th style={{ padding: '6px 8px' }}>Look</th>
                        <th style={{ padding: '6px 8px', width: 50 }}>Type</th>
                        <th style={{ padding: '6px 8px', width: 50 }}>Pose</th>
                        <th style={{ padding: '6px 8px', width: 70, textAlign: 'right' }}>Sheets</th>
                        <th style={{ padding: '6px 8px', width: 150 }}>% of Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.looks.map((look, i) => {
                        const lookData = (allLooks || []).find(l => l.id === look.look_template_id);
                        const isTop3 = i < 3 && look.sheet_count > 0;
                        const notSelling = look.sheet_count === 0;
                        return (
                        <tr key={look.look_template_id} style={{
                          borderBottom: '1px solid #f0f0f0',
                          background: isTop3 ? 'rgba(76, 175, 80, 0.06)' : notSelling ? 'rgba(211, 47, 47, 0.04)' : undefined
                        }}>
                          <td style={{ padding: '6px 8px', color: '#999' }}>{i + 1}</td>
                          <td style={{ padding: '6px 8px' }}>
                            {lookData ? (() => {
                              const isLandscape = (lookData.canvas_width || 0) > (lookData.canvas_height || 0);
                              const tw = 32;
                              const th = isLandscape ? 26 : 40;
                              const pw = isLandscape ? 200 : 160;
                              const ph = isLandscape ? 160 : 200;
                              return (
                              <HoverPreview preview={
                                <div style={{ width: pw, height: ph, background: '#f0f0f0', borderRadius: 4, overflow: 'hidden' }}>
                                  <LookPreviewCanvas look={lookData} width={pw} height={ph} allLooks={allLooks} />
                                </div>
                              }>
                                <div style={{
                                  width: tw, height: th, borderRadius: 3, overflow: 'hidden',
                                  border: '1px solid #ddd', background: '#f0f0f0'
                                }}>
                                  <LookPreviewCanvas look={lookData} width={tw} height={th} allLooks={allLooks} />
                                </div>
                              </HoverPreview>
                              );
                            })() : (
                              <div style={{
                                width: 32, height: 40, borderRadius: 3,
                                border: '1px solid #ddd', background: '#f0f0f0'
                              }} />
                            )}
                          </td>
                          <td style={{ padding: '6px 8px', fontWeight: 500, color: notSelling ? '#999' : undefined }}>
                            <span>{look.name}</span>
                            {isTop3 && (
                              <span style={{
                                marginLeft: 6, fontSize: 9, padding: '1px 5px',
                                background: '#4caf50', color: '#fff', borderRadius: 8,
                                fontWeight: 600, verticalAlign: 'middle'
                              }}>Popular</span>
                            )}
                            {notSelling && (
                              <span style={{
                                marginLeft: 6, fontSize: 9, padding: '1px 5px',
                                background: '#d32f2f', color: '#fff', borderRadius: 8,
                                fontWeight: 600, verticalAlign: 'middle'
                              }}>No Sales</span>
                            )}
                          </td>
                          <td style={{ padding: '6px 8px', color: '#666' }}>{look.look_type}</td>
                          <td style={{ padding: '6px 8px', color: '#666' }}>{look.pose_number}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600 }}>{look.sheet_count}</td>
                          <td style={{ padding: '6px 8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <div style={{ flex: 1, background: '#e0e0e0', borderRadius: 3, height: 6 }}>
                                <div style={barStyle(look.percentage)} />
                              </div>
                              <span style={{ fontSize: 11, color: '#666', minWidth: 40, textAlign: 'right' }}>
                                {look.percentage}%
                              </span>
                            </div>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}

                {/* Backgrounds */}
                <h4 style={{ margin: '0 0 10px', fontSize: 14, color: '#333' }}>
                  Backgrounds by Popularity
                </h4>
                {report.backgrounds.length === 0 ? (
                  <div style={{ padding: 16, color: '#999', fontSize: 12 }}>No background data found for this season.</div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e0e0e0', textAlign: 'left' }}>
                        <th style={{ padding: '6px 8px', width: 30 }}>#</th>
                        <th style={{ padding: '6px 8px', width: 36 }}></th>
                        <th style={{ padding: '6px 8px' }}>Background</th>
                        <th style={{ padding: '6px 8px', width: 70, textAlign: 'right' }}>Sheets</th>
                        <th style={{ padding: '6px 8px', width: 150 }}>% of Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.backgrounds.map((bg, i) => {
                        const isBgTop3 = i < 3 && bg.sheet_count > 0;
                        const bgNotSelling = bg.sheet_count === 0;
                        return (
                        <tr key={bg.background_id} style={{
                          borderBottom: '1px solid #f0f0f0',
                          background: isBgTop3 ? 'rgba(76, 175, 80, 0.06)' : bgNotSelling ? 'rgba(211, 47, 47, 0.04)' : undefined
                        }}>
                          <td style={{ padding: '6px 8px', color: '#999' }}>{i + 1}</td>
                          <td style={{ padding: '6px 8px' }}>
                            {bg.image_url ? (
                              <HoverPreview preview={
                                <img src={bg.image_url} alt={bg.color || bg.letter} style={{
                                  width: 160, height: 160, objectFit: 'cover', borderRadius: 4, display: 'block'
                                }} />
                              }>
                                <div style={{
                                  width: 28, height: 28, borderRadius: 4, overflow: 'hidden',
                                  border: '1px solid #ddd', background: `url(${bg.image_url}) center/cover`
                                }} />
                              </HoverPreview>
                            ) : (
                              <div style={{
                                width: 28, height: 28, borderRadius: 4,
                                border: '1px solid #ddd', background: '#eee',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 9, color: '#999'
                              }}>
                                {bg.letter || bg.color || '?'}
                              </div>
                            )}
                          </td>
                          <td style={{ padding: '6px 8px', fontWeight: 500, color: bgNotSelling ? '#999' : undefined }}>
                            <span>{bg.color || bg.letter || `BG #${bg.background_id}`}</span>
                            {isBgTop3 && (
                              <span style={{
                                marginLeft: 6, fontSize: 9, padding: '1px 5px',
                                background: '#4caf50', color: '#fff', borderRadius: 8,
                                fontWeight: 600, verticalAlign: 'middle'
                              }}>Popular</span>
                            )}
                            {bgNotSelling && (
                              <span style={{
                                marginLeft: 6, fontSize: 9, padding: '1px 5px',
                                background: '#d32f2f', color: '#fff', borderRadius: 8,
                                fontWeight: 600, verticalAlign: 'middle'
                              }}>No Sales</span>
                            )}
                          </td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600 }}>{bg.sheet_count}</td>
                          <td style={{ padding: '6px 8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <div style={{ flex: 1, background: '#e0e0e0', borderRadius: 3, height: 6 }}>
                                <div style={bgBarStyle(bg.percentage)} />
                              </div>
                              <span style={{ fontSize: 11, color: '#666', minWidth: 40, textAlign: 'right' }}>
                                {bg.percentage}%
                              </span>
                            </div>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}

              </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default PopularityReport;
